import { createSelector, createSlice } from '@reduxjs/toolkit';
import { IError, IRecord, ISearchResult, ItemType, ViewType } from 'interfaces';
import uniqBy from 'lodash/uniqBy';

import { itemTypeFilterFn } from 'utils/filter';
import { isThisFulfilledAction, isThisPendingAction, isThisRejectedAction, setValue } from 'utils/redux';
import { sortItemFn } from 'utils/sort';

import { RootState } from '../index';

const name = 'library';

// cacheFolderIds
// has this device previously visited this folder?
// if so, we have a cached result (even if it's empty)

const initialState: {
  cacheFolderIds: (string | null)[];
  currentFolderId: string | null;
  currentPage: number;
  currentUploadingId: string | null;
  error: IError;
  filter: string;
  folders: IRecord[];
  isInited: boolean;
  isLoading: boolean;
  isSyncing: boolean;
  itemCount: number;
  items: IRecord[];
  lastCompletedItemId: string;
  maximizeChecklist: boolean;
  onboardingDemoDocAdded: boolean;
  playerSettingsOpen: boolean;
  searchQuery: string;
  searchResults: ISearchResult[];
  sharedItems: IRecord[];
  showAddNew: boolean;
  showAddNewMenuBackdrop: boolean;
  sort: string;
  totalItemCount: number;
  viewType: ViewType | null;
  hideCanvasNotification?: boolean;
} = {
  cacheFolderIds: [],
  currentFolderId: null,
  currentPage: 1,
  currentUploadingId: null,
  error: {},
  filter: 'all',
  folders: [],
  isInited: false,
  isLoading: false,
  isSyncing: false,
  itemCount: 0,
  items: [],
  lastCompletedItemId: '',
  maximizeChecklist: false,
  onboardingDemoDocAdded: false,
  playerSettingsOpen: false,
  searchQuery: '',
  searchResults: [],
  sharedItems: [],
  showAddNew: false,
  showAddNewMenuBackdrop: false,
  sort: 'added',
  totalItemCount: 0,
  viewType: null
};

const { actions: generatedActions, reducer } = createSlice({
  name,
  initialState,
  reducers: {
    clearSyncing: state => {
      state.isSyncing = false;
    },
    hideAddNew: state => {
      state.showAddNew = false;
      state.showAddNewMenuBackdrop = false;
    },
    itemCompleted: (state, action) => {
      state.lastCompletedItemId = action.payload.itemId;
    },
    maximizeChecklist: (state, action) => {
      state.maximizeChecklist = action.payload;
    },
    reset: () => initialState,
    setCacheFolderId: (state, action) => {
      state.cacheFolderIds = state.cacheFolderIds || [];

      const idsSet = new Set(state.cacheFolderIds);
      idsSet.add(action.payload);

      state.cacheFolderIds = Array.from(idsSet);
    },
    setCurrentFolderId: (state, action) => {
      if (state.currentFolderId !== action.payload) {
        state.currentPage = 1;
      }

      setValue('currentFolderId')(state, action);
    },
    setCurrentUploadingId: (state, action) => setValue('currentUploadingId')(state, action),
    setFolders: (state, action) => {
      const currentFolderId = state.currentFolderId || null;
      const otherFolders = (state.folders || []).filter(folder => folder.parentFolderId !== currentFolderId);

      return { ...state, folders: uniqBy([...(action.payload as IRecord[]), ...otherFolders], 'id') };
    },
    setIsInited: (state, action) => setValue('isInited')(state, action),
    setItem: (state, action) => {
      const currentItems = state.items.map((item: IRecord) => {
        if (action.payload.id === item.id) {
          return { ...item };
        } else {
          return item;
        }
      });

      return { ...state, isInited: true, items: currentItems };
    },
    setItems: (state, action) => {
      const currentFolderId = state.currentFolderId || null;
      const otherItems = (state.items || []).filter(item => item.parentFolderId !== currentFolderId);

      return { ...state, isLoading: false, items: uniqBy([...action.payload, ...otherItems], 'id') };
    },
    setItemCount: (state, action) => {
      return { ...state, itemCount: action.payload };
    },
    setItemSort: (state, action) => {
      return { ...state, sort: action.payload };
    },
    setItemsFilterType: (state, action) => {
      const filterByType = action.payload;
      const sortBy = state.sort;
      const filteredItems = state.items
        .filter(item => item.type === ItemType.Record)
        .filter(itemTypeFilterFn(filterByType, state.currentFolderId))
        .sort(sortItemFn(sortBy));

      state.filter = filterByType;
      state.itemCount = filteredItems.length;
    },
    setPlayerSettingsOpen: (state, action) => {
      state.playerSettingsOpen = action.payload;
    },
    setSyncing: state => {
      state.isSyncing = true;
    },
    showAddNew: state => {
      state.showAddNew = true;
    },
    showAddNewWithBackdrop: state => {
      state.showAddNew = true;
      state.showAddNewMenuBackdrop = true;
    },
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload;
    },
    setOnboardingDemoDocAdded: (state, action) => {
      state.onboardingDemoDocAdded = action.payload;
    },
    togglePlayerSettingOpen: state => {
      state.playerSettingsOpen = !state.playerSettingsOpen;
    },
    setViewType: (state, action) => {
      state.viewType = action.payload;
    },
    hideCanvasNotification: state => {
      state.hideCanvasNotification = true;
    }
  },
  extraReducers: builder => {
    builder
      .addMatcher(isThisPendingAction(name), state => {
        state.isLoading = true;
      })
      .addMatcher(isThisRejectedAction(name), state => {
        state.isLoading = false;
      })
      .addMatcher(isThisFulfilledAction(name), state => {
        state.isLoading = false;
      });
  }
});

export const getById = (itemId: string) =>
  createSelector(
    (state: RootState) => state,
    (state: RootState) => ({ ...state.library.items.filter((item: IRecord) => item.id === itemId)[0] })
  );

export const getFolderById = (folderId: string) =>
  createSelector(
    (state: RootState) => state,
    (state: RootState): IRecord => ({ ...state.library.folders?.filter((folder: IRecord) => folder.id === folderId)[0] })
  );

export const getFolders = createSelector(
  (state: RootState) => state,
  (state: RootState) => state.library.items.filter((item: IRecord) => item.type === ItemType.Folder)
);

export const getSharedItemById = (itemId: string) =>
  createSelector(
    (state: RootState) => state,
    (state: RootState) => {
      const item = state.library.sharedItems?.find((item: IRecord) => item.id === itemId);
      if (item) return { ...item };
      return undefined;
    }
  );

export const getError = (action: string) =>
  createSelector(
    (state: RootState) => state,
    (state: RootState) => state.library.error[action]
  );

const actions = {
  ...generatedActions
};

const selectors = { getById, getFolderById, getFolders, getSharedItemById, getError };

export { actions, selectors };

export default reducer;
